import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Search, Mail, Linkedin, Star, X, Phone, Network } from 'lucide-react';
import { useWorkspaceData } from './AccountsPeopleTabs';
import ContactDetailModal from '../AccountWorkspace/ContactDetailModal';
import {
  FILTERS, applyContactFilter, filterCounts, sortContacts,
  displayName, initials, avatarTone, isKeyPerson,
} from './contactsDirectory';
import './contactsDirectory.css';

/*
 * AccountsContactsDirectory — every known person at this account, searchable.
 *
 * The org chart answers "who reports to whom". This answers "where is that
 * person", which is a different job and was previously served by a list mode
 * inside the chart — scannable, but not searchable, and the reason a rep with
 * 39 contacts fell back to Cmd-F.
 *
 * It reads the SAME `/api/workspace` query key the chart and the Meetings tab
 * use, so putting both widgets on the People board costs one fetch, and a
 * contact edited in either place is reconciled in both.
 *
 * WHAT IT DOES NOT SHOW is as deliberate as what it does — see
 * `contactsDirectory.js` for the field counts behind it. No phone column, no
 * org-unit column, no relationship filter, no Gravatar.
 */

/** Rendered per row; the modal is heavy and mounts once, for the open one. */
function ContactRow({ contact, active, onOpen, onFocus, onShowInChart }) {
  const name = displayName(contact);
  const key = isKeyPerson(contact);
  return (
    <li className="acd-row-wrap">
      <button
        type="button"
        className={`acd-row${active ? ' is-active' : ''}`}
        onClick={() => onOpen(contact)}
        /*
         * NO onMouseEnter. It used to move the cursor, and `.acd-row:hover`
         * already carries the identical background, so the only things that
         * write bought were two bugs: the pointer crossing the list announced
         * every row it passed to a screen reader, and — worse — a mouse
         * resting anywhere over the list silently became the target of the
         * search box's Enter. Arrow to the third person, brush the pointer
         * past the twentieth, press Enter, open the twentieth.
         *
         * Taking focus, by contrast, DOES retire the cursor: otherwise tabbing
         * into the list leaves a highlight on one row and a focus ring on
         * another — only one of them openable, but two things on screen
         * claiming to be where you are.
         */
        onFocus={onFocus}
        /*
         * The row is the control, and the mail/LinkedIn links sit INSIDE it.
         * A button cannot contain a link, so those are rendered as siblings in
         * the grid rather than nested — see the `acd-row-actions` element
         * after this one.
         */
        aria-label={`${name}${contact.title ? `, ${contact.title}` : ''}`}
      >
        <span className={`acd-avatar acd-avatar--t${avatarTone(contact)}`} aria-hidden="true">
          {initials(contact)}
        </span>
        <span className="acd-who">
          <span className="acd-name">
            {name}
            {key && (
              <Star size={12} className="acd-key" aria-label="Key contact" />
            )}
          </span>
          {contact.title && <span className="acd-title">{contact.title}</span>}
          {(contact.org_unit || contact.phone) && (
            <span className="acd-meta">
              {contact.org_unit}
              {contact.org_unit && contact.phone ? ' · ' : ''}
              {contact.phone && (
                <>
                  <Phone size={10} aria-hidden="true" />
                  {' '}
                  {contact.phone}
                </>
              )}
            </span>
          )}
        </span>
      </button>
      <span className="acd-row-actions">
        {/* Beside the row, never inside it: the row is a <button> and cannot
            contain another one. This is the "move into the org chart" half of
            the split view — the directory answers WHERE a person is, the chart
            answers WHO THEY REPORT TO, and this is the hinge between them. */}
        {onShowInChart && (
          <button
            type="button"
            className="acd-act acd-act--chart"
            onClick={() => onShowInChart(contact)}
            aria-label={`Show ${name} in the org chart`}
            title="Show in the org chart"
          >
            <Network size={14} />
          </button>
        )}
        {contact.email && (
          <a
            className="acd-act"
            href={`mailto:${contact.email}`}
            title={contact.email}
            aria-label={`Email ${name}`}
          >
            <Mail size={14} />
          </a>
        )}
        {contact.linkedin_url && (
          <a
            className="acd-act"
            href={contact.linkedin_url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`${name} on LinkedIn`}
          >
            <Linkedin size={14} />
          </a>
        )}
      </span>
    </li>
  );
}

export default function AccountsContactsDirectory({
  selectedDepartment, compact = false, onShowInChart = null,
}) {
  const { data, isLoading, error, refetch } = useWorkspaceData(selectedDepartment);
  const queryClient = useQueryClient();
  const [query, setQuery] = useState('');
  const [filters, setFilters] = useState([]);
  const [cursor, setCursor] = useState(-1);
  const [open, setOpen] = useState(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const all = useMemo(() => sortContacts(data?.contacts || []), [data]);
  // Counts describe THIS view — see filterCounts. They have to depend on the
  // query and the other chips, or they describe an account nobody is looking at.
  const counts = useMemo(() => filterCounts(all, { query, filters }), [all, query, filters]);
  const rows = useMemo(
    () => applyContactFilter(all, { query, filters }),
    [all, query, filters],
  );

  // A narrowing search must never leave the cursor pointing past the end.
  useEffect(() => { setCursor((c) => (c >= rows.length ? rows.length - 1 : c)); }, [rows.length]);

  const toggle = useCallback((key) => {
    setFilters((f) => (f.includes(key) ? f.filter((x) => x !== key) : [...f, key]));
  }, []);

  const onKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      setCursor((c) => {
        const next = e.key === 'ArrowDown' ? c + 1 : c - 1;
        if (next < 0) return -1;
        return Math.min(next, rows.length - 1);
      });
      return;
    }
    if (e.key === 'Enter' && cursor >= 0 && rows[cursor]) {
      e.preventDefault();
      setOpen(rows[cursor]);
      return;
    }
    if (e.key === 'Escape' && query) {
      e.preventDefault();
      setQuery('');
      setCursor(-1);
    }
  }, [rows, cursor, query]);

  // Keep the highlighted row on screen when it moves by keyboard.
  useEffect(() => {
    if (cursor < 0 || !listRef.current) return;
    const el = listRef.current.querySelectorAll('.acd-row')[cursor];
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  if (isLoading) return <div className="acd-note">Loading contacts…</div>;
  if (error) {
    return (
      <div className="acd-note acd-note--error">
        Contacts unavailable — {error.message || 'request failed'}
        <button type="button" className="acd-retry" onClick={() => refetch()}>Retry</button>
      </div>
    );
  }
  if (all.length === 0) {
    return (
      <div className="acd-note">
        No contacts on this account yet. Add one from the People widget, or claim a
        harvested person from the contact inbox.
      </div>
    );
  }

  return (
    <div className={`acd${compact ? ' acd--compact' : ''}`}>
      <div className="acd-bar">
        <div className="acd-search">
          <Search size={14} className="acd-search-icon" aria-hidden="true" />
          <input
            ref={inputRef}
            type="search"
            className="acd-input"
            placeholder={`Search ${all.length} ${all.length === 1 ? 'person' : 'people'}…`}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(-1); }}
            onKeyDown={onKeyDown}
            aria-label="Search contacts"
          />
          {query && (
            <button
              type="button"
              className="acd-clear"
              onClick={() => { setQuery(''); setCursor(-1); inputRef.current?.focus(); }}
              aria-label="Clear search"
            >
              <X size={13} />
            </button>
          )}
        </div>
        <div className="acd-chips">
          {Object.keys(FILTERS).map((key) => (
            <button
              key={key}
              type="button"
              className={`acd-chip${filters.includes(key) ? ' is-on' : ''}`}
              // A chip that would select nothing is shown and disabled rather
              // than hidden: "no key people recorded here" is a fact about the
              // account worth seeing, and a row of chips that changes shape
              // between accounts is harder to use than one that does not.
              //
              // An ACTIVE chip is never disabled, however low its count falls.
              // Now that the counts follow the query, an on chip can reach 0 —
              // turn on Key people, then search for someone who is not one —
              // and disabling it there would strand the reader holding a
              // filter they cannot switch off.
              disabled={!filters.includes(key) && counts[key] === 0}
              aria-pressed={filters.includes(key)}
              onClick={() => toggle(key)}
            >
              {FILTERS[key].label}
              <span className="acd-chip-n">{counts[key]}</span>
            </button>
          ))}
        </div>
      </div>

      {/*
        * Arrow keys in the search box move a HIGHLIGHT, which is a colour
        * change and nothing else — a screen-reader user pressing ArrowDown was
        * told nothing at all and then Enter opened a person they had never
        * heard named. The list cannot carry `role="listbox"` to fix this:
        * options must not contain focusable elements, and every row here is a
        * button with mail and LinkedIn links beside it. So the highlight is
        * announced instead, with its position, which is what the listbox
        * pattern would have said anyway.
        */}
      <div className="sr-only" role="status">
        {cursor >= 0 && rows[cursor]
          ? `${displayName(rows[cursor])}${rows[cursor].title ? `, ${rows[cursor].title}` : ''}, ${cursor + 1} of ${rows.length}`
          : ''}
      </div>

      <div className="acd-count" aria-live="polite">
        {rows.length === all.length
          ? `${all.length} ${all.length === 1 ? 'person' : 'people'}`
          : `${rows.length} of ${all.length}`}
      </div>

      {rows.length === 0 ? (
        <div className="acd-note">
          Nothing matches{query ? ` “${query}”` : ''}
          {filters.length ? ' with those filters' : ''}.
        </div>
      ) : (
        /*
         * NO onKeyDown here, deliberately. The list is not focusable itself, so
         * the only events this ever saw were ones bubbling up from a row button
         * a user had TABBED to — and handling them made the cursor and DOM
         * focus two competing answers to "which row am I on". ArrowDown moved
         * the highlight to row 1 while focus sat on row 5, and Enter then
         * opened row 1 AND preventDefault()'d the focused button's own click.
         * A tabbed-to row is opened by its own button; the cursor belongs to
         * the search box alone.
         */
        <ul className="acd-list" ref={listRef}>
          {rows.map((c, i) => (
            <ContactRow
              key={c.id || `${displayName(c)}-${i}`}
              contact={c}
              active={i === cursor}
              onOpen={setOpen}
              onFocus={() => setCursor(-1)}
              onShowInChart={onShowInChart}
            />
          ))}
        </ul>
      )}

      {open && (
        <ContactDetailModal
          contact={open}
          allContacts={all}
          onClose={() => setOpen(null)}
          // The chart, this list and the Meetings tab share one query key, so
          // an edit made here reconciles in all three rather than leaving the
          // org chart showing the old title until a reload.
          onUpdate={() => queryClient.invalidateQueries({ queryKey: ['workspace'] })}
        />
      )}
    </div>
  );
}
